/**
 * Quick-access accessibility + appearance panel.
 *
 * The full settings live at /settings#accessibility, but those are
 * three taps deep — too far for a doctor mid-consult who suddenly
 * needs the text bigger or wants to flip to dark mode for a bright
 * exam-room screen. This panel surfaces the high-frequency controls
 * one click from anywhere in the app.
 *
 * Implemented as a Sheet so we get keyboard semantics (focus trap,
 * Esc, return-focus) and a single layout that works on phone +
 * desktop. The trigger lives in the Header — see Header.tsx.
 */

import React from 'react';
import {
  Eye,
  Languages,
  Sun,
  Moon,
  Monitor,
  Sparkles,
  Settings as SettingsIcon,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
  SheetHeader,
} from '@/components/ui/sheet';
import { useAccessibilityStore } from '@/stores/useAccessibilityStore';
import { useLocalizationStore } from '@/stores/useLocalizationStore';
import { useTranslation } from '@/hooks/useTranslation';
import { useNavigate } from 'react-router-dom';

interface AccessibilityQuickPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const Toggle: React.FC<{
  on: boolean;
  onChange: () => void;
  label: string;
}> = ({ on, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={onChange}
    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
    style={{
      backgroundColor: on ? 'var(--color-primary-600)' : 'var(--color-bg-tertiary)',
      border: '1px solid var(--color-border-primary)',
    }}
  >
    <span
      className="inline-block h-4 w-4 transform rounded-full transition-transform"
      style={{
        backgroundColor: '#ffffff',
        transform: on ? 'translateX(22px)' : 'translateX(2px)',
        boxShadow: 'var(--shadow-sm)',
      }}
    />
  </button>
);

interface OptionButtonProps {
  selected: boolean;
  onClick: () => void;
  label: string;
  icon?: React.ReactNode;
  ariaLabel?: string;
}
const OptionButton: React.FC<OptionButtonProps> = ({
  selected,
  onClick,
  label,
  icon,
  ariaLabel,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    aria-label={ariaLabel ?? label}
    onClick={onClick}
    className="rounded-lg px-3 py-2.5 text-sm transition-colors min-h-11 inline-flex items-center justify-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
    style={{
      backgroundColor: selected
        ? 'color-mix(in srgb, var(--color-primary-500) 10%, var(--color-bg-primary))'
        : 'var(--color-bg-primary)',
      border: selected
        ? '1px solid var(--color-primary-500)'
        : '1px solid var(--color-border-primary)',
      color: selected ? 'var(--color-primary-700)' : 'var(--color-text-primary)',
      fontWeight: selected ? 600 : 500,
    }}
  >
    {icon}
    {label}
  </button>
);

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p
    className="text-xs font-semibold mb-2"
    style={{ color: 'var(--color-text-tertiary)' }}
  >
    {children}
  </p>
);

export const AccessibilityQuickPanel: React.FC<AccessibilityQuickPanelProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const { t, isRTL } = useTranslation();

  const prefs = useAccessibilityStore((s) => s.prefs);
  const setPrefs = useAccessibilityStore((s) => s.setPrefs);

  const language = useLocalizationStore((s) => s.config.language);
  const setLanguage = useLocalizationStore((s) => s.setLanguage);

  const handleOpenFull = () => {
    onOpenChange(false);
    navigate('/settings#accessibility');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isRTL ? 'left' : 'right'}
        className="w-full sm:max-w-sm p-0 flex flex-col gap-0"
        style={{ backgroundColor: 'var(--color-bg-primary)' }}
      >
        <SheetHeader
          className="px-5 py-4 border-b"
          style={{ borderColor: 'var(--color-border-primary)' }}
        >
          <SheetTitle className="text-base font-semibold tracking-tight inline-flex items-center gap-2">
            <Eye className="w-4 h-4" style={{ color: 'var(--color-primary-700)' }} />
            {t('accessibilityQuick.title')}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {t('accessibilityQuick.subtitle')}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Language */}
          <section>
            <FieldLabel>
              <span className="inline-flex items-center gap-1.5">
                <Languages className="w-3.5 h-3.5" />
                {t('accessibilityQuick.language')}
              </span>
            </FieldLabel>
            <div role="radiogroup" aria-label={t('accessibilityQuick.language')} className="grid grid-cols-2 gap-2">
              <OptionButton
                selected={language === 'en'}
                onClick={() => setLanguage('en')}
                label="English"
              />
              <OptionButton
                selected={language === 'ar'}
                onClick={() => setLanguage('ar')}
                label="العربية"
              />
            </div>
          </section>

          {/* Appearance (color scheme) */}
          <section>
            <FieldLabel>
              <span className="inline-flex items-center gap-1.5">
                <Sun className="w-3.5 h-3.5" />
                {t('accessibility.colorScheme.label')}
              </span>
            </FieldLabel>
            <div role="radiogroup" aria-label={t('accessibility.colorScheme.label')} className="grid grid-cols-3 gap-2">
              <OptionButton
                selected={prefs.colorScheme === 'light'}
                onClick={() => setPrefs({ colorScheme: 'light' })}
                label={t('accessibility.colorScheme.light')}
                icon={<Sun className="w-3.5 h-3.5" />}
              />
              <OptionButton
                selected={prefs.colorScheme === 'dark'}
                onClick={() => setPrefs({ colorScheme: 'dark' })}
                label={t('accessibility.colorScheme.dark')}
                icon={<Moon className="w-3.5 h-3.5" />}
              />
              <OptionButton
                selected={prefs.colorScheme === 'system'}
                onClick={() => setPrefs({ colorScheme: 'system' })}
                label={t('accessibility.colorScheme.system')}
                icon={<Monitor className="w-3.5 h-3.5" />}
              />
            </div>
          </section>

          {/* Text size */}
          <section>
            <FieldLabel>{t('accessibility.textScale.label')}</FieldLabel>
            <div role="radiogroup" aria-label={t('accessibility.textScale.label')} className="grid grid-cols-4 gap-2">
              {(
                [
                  { v: 'normal', sub: '100%' },
                  { v: 'large', sub: '115%' },
                  { v: 'xlarge', sub: '130%' },
                  { v: 'xxlarge', sub: '150%' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  role="radio"
                  aria-checked={prefs.textScale === opt.v}
                  aria-label={`${t('accessibility.textScale.label')} ${opt.sub}`}
                  onClick={() => setPrefs({ textScale: opt.v })}
                  className="rounded-lg px-2 py-2 text-xs transition-colors min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{
                    backgroundColor:
                      prefs.textScale === opt.v
                        ? 'color-mix(in srgb, var(--color-primary-500) 10%, var(--color-bg-primary))'
                        : 'var(--color-bg-primary)',
                    border:
                      prefs.textScale === opt.v
                        ? '1px solid var(--color-primary-500)'
                        : '1px solid var(--color-border-primary)',
                    color:
                      prefs.textScale === opt.v
                        ? 'var(--color-primary-700)'
                        : 'var(--color-text-primary)',
                    fontWeight: prefs.textScale === opt.v ? 600 : 500,
                  }}
                >
                  <div>A</div>
                  <div className="text-[10px] opacity-80 mt-0.5">{opt.sub}</div>
                </button>
              ))}
            </div>
          </section>

          {/* Layout direction override */}
          <section>
            <FieldLabel>{t('accessibility.direction.label')}</FieldLabel>
            <div role="radiogroup" aria-label={t('accessibility.direction.label')} className="grid grid-cols-3 gap-2">
              <OptionButton
                selected={prefs.direction === 'auto'}
                onClick={() => setPrefs({ direction: 'auto' })}
                label={t('accessibility.direction.auto')}
              />
              <OptionButton
                selected={prefs.direction === 'ltr'}
                onClick={() => setPrefs({ direction: 'ltr' })}
                label="LTR"
              />
              <OptionButton
                selected={prefs.direction === 'rtl'}
                onClick={() => setPrefs({ direction: 'rtl' })}
                label="RTL"
              />
            </div>
          </section>

          {/* Toggles */}
          <section className="space-y-3">
            <div
              className="flex items-center justify-between gap-4 p-3 rounded-lg"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-primary)',
              }}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  {t('accessibility.contrast.label')}
                </p>
                <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('accessibility.contrast.description')}
                </p>
              </div>
              <Toggle
                on={prefs.contrast === 'high'}
                onChange={() =>
                  setPrefs({ contrast: prefs.contrast === 'high' ? 'normal' : 'high' })
                }
                label={t('accessibility.contrast.label')}
              />
            </div>

            <div
              className="flex items-center justify-between gap-4 p-3 rounded-lg"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-primary)',
              }}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  {t('accessibility.reduceMotion.label')}
                </p>
                <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('accessibility.reduceMotion.description')}
                </p>
              </div>
              <Toggle
                on={prefs.reduceMotion}
                onChange={() => setPrefs({ reduceMotion: !prefs.reduceMotion })}
                label={t('accessibility.reduceMotion.label')}
              />
            </div>
          </section>
        </div>

        <div
          className="border-t px-5 py-3"
          style={{ borderColor: 'var(--color-border-primary)' }}
        >
          <button
            type="button"
            onClick={handleOpenFull}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              border: '1px solid var(--color-border-primary)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <SettingsIcon className="w-4 h-4" />
            {t('accessibilityQuick.openFull')}
            <Sparkles className="w-3 h-3 opacity-50" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
